"use client"

import * as React from "react"
import { Link } from "react-router"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import { formatTermDateRange } from "@/lib/evaluation-term-utils"
import { evaluationAnalyticsPath } from "@/lib/evaluation-routes"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"
import { useProduct } from "@/contexts/product-context"

interface EvaluationAnalyticFocusTermProps {
  term: EvaluationTerm
}

export function EvaluationAnalyticFocusTerm({ term }: EvaluationAnalyticFocusTermProps) {
  const { product } = useProduct()

  return (
    <Card size="sm" id={`term-${term.id}`} className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {term.programName}
            </p>
            <h3 className="text-lg font-semibold leading-snug text-foreground font-heading">
              {term.academicYear} · {term.termName}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {formatTermDateRange(term.startDate, term.endDate)}
              {term.courseOfferings > 0 ? ` · ${term.courseOfferings} courses` : null}
            </p>
          </div>
          {term.needsAttention ? (
            <StatusBadge
              label="Needs attention"
              tone="warning"
              icon="fa-triangle-exclamation"
              size="md"
            />
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {term.ratings ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <FocusMetric
              label="Response rate"
              value={term.closedResponseRate != null ? `${term.closedResponseRate}%` : "None"}
            />
            <FocusMetric label="Program avg" value={term.ratings.programAverage.toFixed(1)} />
            <FocusMetric label="Course avg" value={term.ratings.courseAverage.toFixed(1)} />
            <FocusMetric label="Faculty avg" value={term.ratings.facultyAverage.toFixed(1)} />
          </div>
        ) : null}

        {term.attention ? (
          <div className="rounded-lg border border-chart-4/30 bg-chart-4/5 px-3 py-2.5 text-sm text-foreground">
            {term.attention.coursesBelowThreshold} courses and{" "}
            {term.attention.facultyRolesBelowThreshold} faculty roles scored below threshold.
          </div>
        ) : null}
      </CardContent>

      <CardFooter className="mt-auto flex flex-wrap gap-2 border-t border-border/60 pt-4">
        <Button size="sm" asChild>
          <Link to={evaluationAnalyticsPath(product, term.id)}>View analytics</Link>
        </Button>
        <Button size="sm" variant="outline">
          Export summary
        </Button>
      </CardFooter>
    </Card>
  )
}

function FocusMetric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xl font-semibold tabular-nums text-foreground">{value}</span>
    </div>
  )
}
